import type { Building } from "building-types/building/buildingTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { OpeningPlacementReturnValue } from "building-types/openings/openingPlacementTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuiltinWindowId } from "building-types/windows/windowId";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { getDoorCatalog } from "doc/elements/doors/doorCatalog";
import { getDoorHomeSettings } from "doc/elements/doors/doorHomeSettings";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { getWindowCatalog } from "doc/elements/window/windowCatalog";
import { getWindowHomeSettings } from "doc/elements/window/windowHomeSettings";
import { getMergeSettingsForOpeningParamsFromBuilding } from "doc/mergeSettingsSet";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import type { OpeningPlacementFuncOptions } from "../elements/openings/openingPlacementFuncOptions";
import { swingSideMirror } from "../elements/openings/openingUtils";
import {
  isCombinedGlobalInstanceMirror,
  resolveManualOpenings,
  resolveRoomTypeOpenings,
} from "../generationTools/generateOpeningsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  levels,
  levelsAttributes,
  planarGraphs,
  rooms,
  roomLevelIndices,
  roomsByLevel,
  roomFloorAssemblies,
  windowPlacements: windowPlacementsFromBuilding,
  planarGraphPerimeters,
  settings,
  mirror,
  openingPlacements,
  wallMasses,
  roomIndex,
  settingsSets,
  optionChoices,
  doc,
}: Building) {
  const { optionSets, optionChoices: docOptionChoices } = doc;

  assertIsNotNullOrUndefined(settings);
  assertIsNotNullOrUndefined(settingsSets);
  assertIsNotNullOrUndefined(optionChoices);

  return {
    levels,
    levelsAttributes,
    planarGraphs,
    rooms,
    roomLevelIndices,
    roomsByLevel,
    roomFloorAssemblies,
    windowPlacementsFromBuilding,
    planarGraphPerimeters,
    doorCatalogSettings: settings.doorCatalog,
    windowCatalogSettings: settings.windowCatalog,
    mirror,
    openingPlacements,
    wallMasses,
    roomIndex,
    mergeSettingsForOpeningParams: getMergeSettingsForOpeningParamsFromBuilding(
      { settingsSets, optionChoices, settings },
      doc,
    ),
    windowHomeSettings: getWindowHomeSettings(settings),
    doorHomeSettings: getDoorHomeSettings(settings),
    optionSets,
    optionChoices: docOptionChoices,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  windowHomeSettings: "shallow",
  doorHomeSettings: "shallow",
  mergeSettingsForOpeningParams: "deep",
});

function generate({
  rooms,
  planarGraphs,
  roomLevelIndices,
  roomsByLevel,
  levels,
  levelsAttributes,
  windowPlacementsFromBuilding,
  planarGraphPerimeters,
  doorCatalogSettings,
  windowCatalogSettings,
  mirror,
  openingPlacements,
  roomFloorAssemblies,
  wallMasses,
  roomIndex,
  doorHomeSettings,
  windowHomeSettings,
  mergeSettingsForOpeningParams,
  optionSets,
  optionChoices,
}: ReturnType<typeof select>): {
  doorPlacements: Table<GeneratedDoorPlacement>;
  windowPlacements: Table<GeneratedWindowPlacement>;
  issues: BuildingGenerationIssue[];
} {
  const issues: BuildingGenerationIssue[] = [];
  const windowPlacements: RWTable<GeneratedWindowPlacement> = { ...windowPlacementsFromBuilding };
  const doorPlacements: RWTable<GeneratedDoorPlacement> = {};
  const doorCatalog = getDoorCatalog(doorCatalogSettings);
  const windowCatalog = getWindowCatalog(windowCatalogSettings);

  assertIsNotNullOrUndefined(levelsAttributes);
  assertIsNotNullOrUndefined(planarGraphs);
  assertIsNotNullOrUndefined(roomsByLevel);
  assertIsNotNullOrUndefined(rooms);
  assertIsNotNullOrUndefined(planarGraphPerimeters);
  assertIsNotNullOrUndefined(roomLevelIndices);
  assertIsNotNullOrUndefined(openingPlacements);
  assertIsNotNullOrUndefined(roomFloorAssemblies);
  assertIsNotNullOrUndefined(roomIndex);
  assertIsNotNullOrUndefined(mirror);
  assertIsNotNullOrUndefined(wallMasses);

  // for each level
  for (const levelId in levels) {
    const level = levels[levelId];
    const levelAttributes = levelsAttributes[levelId];
    const levelRooms = roomsOnLevel(levelId, rooms, roomsByLevel);
    const planarGraph = planarGraphs[levelId];
    const planarGraphPerimeter = planarGraphPerimeters[levelId];
    const perimeterStreetOrientationTable = planarGraphPerimeter.coveredPerimeterStreetOrientations;
    const wallLayerExtentsCache: WallLayerExtentsCache = {};

    // roomId => [door, precedence, inserted]
    const deferredDoorInsertions: { [roomId: number]: [GeneratedDoorPlacement, number, boolean][] } = {};
    for (const room of levelRooms) {
      deferredDoorInsertions[roomLevelIndices[room.id]] = [];
    }

    const levelOpeningPlacements = openingPlacements[levelId] || [];

    for (const openingPlacementContext of levelOpeningPlacements) {
      const { roomStartId, roomEndId, halfEdgeIndex, openingPlacement, wallIndex, streetOrientation } =
        openingPlacementContext;

      const roomStart = roomStartId !== undefined ? rooms[roomStartId] : undefined;
      const roomEnd = roomEndId !== undefined ? rooms[roomEndId] : undefined;

      // the full `OpeningPlacementFuncOptions` are necessary for placing openings
      const opts: OpeningPlacementFuncOptions = {
        roomStart,
        roomEnd,
        wallIndex,
        halfEdgeIndex,
        planarGraph,
        roomLevelIndices,
        roomFloorAssemblies,
        level,
        levelAttributes,
        streetOrientation,
        planarGraphPerimeter,
        perimeterStreetOrientationTable,
        roomIndex,
        windowCatalog,
        doorCatalog,
        windowHomeSettings,
        doorHomeSettings,
        mergeSettingsForOpeningParams,
        optionSets,
        optionChoices,
      };

      const oppositeRoomIndex = roomEndId ? roomLevelIndices[roomEndId] : -1;

      let result: OpeningPlacementReturnValue | null;
      switch (openingPlacement.type) {
        case "ROOM_TYPE": {
          result = resolveRoomTypeOpenings(openingPlacement, opts);
          break;
        }
        case "MANUAL": {
          result = resolveManualOpenings({
            placement: openingPlacement,
            opts,
            windowCatalog,
            doorCatalog,
            mirror: isCombinedGlobalInstanceMirror(mirror, roomStart),
            issues,
            wallMasses,
            wallLayerExtentsCache,
            levelRooms,
          });
          break;
        }
      }

      if (!result) {
        continue;
      }

      if (result.doorPlacements) {
        for (const door of result.doorPlacements) {
          if (oppositeRoomIndex === -1) {
            doorPlacements[door.id] = door;
          } else if (roomStart && roomStart.attributes.singleDoor) {
            const doorTuple: [GeneratedDoorPlacement, number, boolean] = [
              door,
              openingPlacement.precedence || -1,
              false,
            ];
            deferredDoorInsertions[roomLevelIndices[roomStart.id]].push(doorTuple);
          } else if (roomEnd && roomEnd.attributes.singleDoor) {
            const doorTuple: [GeneratedDoorPlacement, number, boolean] = [
              door,
              openingPlacement.precedence || -1,
              false,
            ];
            deferredDoorInsertions[roomLevelIndices[roomEnd.id]].push(doorTuple);
          } else {
            doorPlacements[door.id] = door;
          }
          if (door.handle) {
            BuildingGenerationContext.indexAddNode(door.handle);
          }
        }
      }

      if (result.windowPlacements) {
        for (const window of result.windowPlacements) {
          if (window.descriptor.id === BuiltinWindowId.NO) {
            continue;
          }
          windowPlacements[window.id] = window;
          if (window.handle) {
            BuildingGenerationContext.indexAddNode(window.handle);
          }
        }
      }
    }

    // handle all door insertions by precedence
    for (const roomId in deferredDoorInsertions) {
      const doorTuples = deferredDoorInsertions[roomId];

      if (doorTuples.length === 0) {
        continue;
      }

      // sort by decreasing precedence
      doorTuples.sort((a, b) => b[1] - a[1]);

      const door = doorTuples[0][0];
      doorPlacements[door.id] = door;
    }
  } // levels

  // Handle reflection of room-type door and window placements
  for (const doorId in doorPlacements) {
    const door = doorPlacements[doorId];
    if (door.handle) {
      continue;
    }
    if (isCombinedGlobalInstanceMirror(mirror, door.frontRoom ?? door.backRoom)) {
      doorPlacements[doorId] = { ...door, param: -door.param, swingSide: swingSideMirror(door.swingSide) };
    }
  }

  for (const windowId in windowPlacements) {
    const window = windowPlacements[windowId];
    if (window.handle) {
      continue;
    }
    if (isCombinedGlobalInstanceMirror(mirror, window.frontRoom ?? window.backRoom)) {
      windowPlacements[windowId] = { ...window, param: -window.param };
    }
  }

  return { doorPlacements, windowPlacements, issues };
}

export default memo(select, generate, compare);
